/**
 * ZoomControl - Controles de zoom personalizados PTEL
 * 
 * F8.02 - Controles interactivos del visor Leaflet
 * 
 * Botones accesibles para zoom in/out/reset con estilo PTEL Design System.
 * Reemplaza los controles nativos de Leaflet (zoomControl={false}).
 * 
 * @module components/map/controls/ZoomControl
 */

import React, { memo, useCallback, useRef, useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import { DEFAULT_ZOOM, ZOOM_RANGE } from '../constants';

// =============================================================================
// TIPOS
// =============================================================================

export interface ZoomControlProps {
  /** Posición del control */
  position?: 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
  /** Mostrar botón de reset */
  showReset?: boolean;
  /** Zoom al resetear (default: DEFAULT_ZOOM) */
  resetZoom?: number;
  /** Clases CSS adicionales */
  className?: string;
}

// =============================================================================
// COMPONENTE
// =============================================================================

export const ZoomControl = memo<ZoomControlProps>(function ZoomControl({
  position = 'bottomright',
  showReset = true,
  resetZoom = DEFAULT_ZOOM,
  className = '',
}) {
  const map = useMap();
  const [currentZoom, setCurrentZoom] = useState(map.getZoom());
  const containerRef = useRef<HTMLDivElement>(null);

  // Sincronizar zoom actual
  useEffect(() => {
    const handleZoom = () => setCurrentZoom(map.getZoom());
    map.on('zoomend', handleZoom);
    return () => { map.off('zoomend', handleZoom); };
  }, [map]);

  // Evitar propagación de eventos al mapa
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener('dblclick', stop);
    el.addEventListener('mousedown', stop);
    el.addEventListener('wheel', stop);
    return () => {
      el.removeEventListener('dblclick', stop);
      el.removeEventListener('mousedown', stop);
      el.removeEventListener('wheel', stop);
    };
  }, []);

  const handleZoomIn = useCallback(() => {
    map.zoomIn();
  }, [map]);

  const handleZoomOut = useCallback(() => {
    map.zoomOut();
  }, [map]);

  const handleReset = useCallback(() => {
    map.setZoom(resetZoom);
  }, [map, resetZoom]);

  const isMinZoom = currentZoom <= (ZOOM_RANGE.min);
  const isMaxZoom = currentZoom >= (ZOOM_RANGE.max);

  // Posición CSS
  const posStyle: React.CSSProperties = {
    position: 'absolute',
    zIndex: 1000,
    ...(position.includes('top') ? { top: 10 } : { bottom: 10 }),
    ...(position.includes('right') ? { right: 10 } : { left: 10 }),
  };

  return (
    <div
      ref={containerRef}
      className={`ptel-zoom-control ${className}`}
      style={posStyle}
      role="group"
      aria-label="Controles de zoom del mapa"
    >
      <button
        className="ptel-zoom-control__btn"
        onClick={handleZoomIn}
        disabled={isMaxZoom}
        aria-label={`Acercar zoom (nivel actual: ${currentZoom})`}
        title="Acercar"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      {showReset && (
        <button
          className="ptel-zoom-control__btn ptel-zoom-control__btn--reset"
          onClick={handleReset}
          aria-label="Restablecer zoom a vista general"
          title="Vista general"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="8" cy="8" r="2" fill="currentColor" />
          </svg>
        </button>
      )}

      <button
        className="ptel-zoom-control__btn"
        onClick={handleZoomOut}
        disabled={isMinZoom}
        aria-label={`Alejar zoom (nivel actual: ${currentZoom})`}
        title="Alejar"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
});

export default ZoomControl;
